import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import CloseIcon from '@mui/icons-material/Close';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW,
} from 'lexical';
import useThemeStyles from '../../../../../hooks/useThemeStyles';
import analysisService from '../../../../../utils/apiRequests/analysisService';

// Selections shorter than this are too small for meaningful sentiment
const MIN_SELECTION_LENGTH = 12;
const MAX_SELECTION_LENGTH = 20000;

const SENTIMENT_META = {
  positive: { label: 'Positive', color: '#22c55e' },
  negative: { label: 'Negative', color: '#ef4444' },
  neutral: { label: 'Neutral', color: '#94a3b8' },
};

const getDomSelectionRect = () => {
  const domSelection = window.getSelection();
  if (!domSelection || domSelection.rangeCount === 0 || domSelection.isCollapsed) {
    return null;
  }
  const rect = domSelection.getRangeAt(0).getBoundingClientRect();
  if (!rect || (rect.width === 0 && rect.height === 0)) {
    return null;
  }
  return rect;
};

/**
 * Lets the writer highlight a block of text and run sentiment analysis on it.
 * A small "Analyze tone" chip floats above the selection; clicking it opens a
 * panel with the detected sentiment and confidence breakdown.
 */
const SentimentPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const { colors, isDark } = useThemeStyles();

  const [anchor, setAnchor] = useState(null); // { top, left, bottom }
  const [selectedText, setSelectedText] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const panelOpenRef = useRef(false);
  panelOpenRef.current = panelOpen;

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    setResult(null);
    setError('');
    setLoading(false);
  }, []);

  const updateFromSelection = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        // Keep the results panel up while the user reads it; only hide the chip.
        if (!panelOpenRef.current) {
          setAnchor(null);
          setSelectedText('');
        }
        return;
      }

      const text = selection.getTextContent().trim();
      if (text.length < MIN_SELECTION_LENGTH || text.length > MAX_SELECTION_LENGTH) {
        if (!panelOpenRef.current) {
          setAnchor(null);
          setSelectedText('');
        }
        return;
      }

      const rect = getDomSelectionRect();
      if (!rect) return;

      // A new highlight replaces any previous result
      if (panelOpenRef.current) {
        setPanelOpen(false);
        setResult(null);
        setError('');
      }
      setSelectedText(text);
      setAnchor({ top: rect.top, left: rect.left + rect.width / 2, bottom: rect.bottom });
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateFromSelection();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, updateFromSelection]);

  // Keep the floating UI glued to the selection while scrolling/resizing
  useEffect(() => {
    if (!anchor) return undefined;
    const reposition = () => {
      const rect = getDomSelectionRect();
      if (rect) {
        setAnchor({ top: rect.top, left: rect.left + rect.width / 2, bottom: rect.bottom });
      }
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [anchor !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape closes everything
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePanel();
        setAnchor(null);
        setSelectedText('');
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closePanel]);

  const handleAnalyze = useCallback(async () => {
    if (!selectedText) return;
    setPanelOpen(true);
    setLoading(true);
    setResult(null);
    setError('');

    const { data, error: requestError } = await analysisService.analyzeSentiment(selectedText);
    setLoading(false);

    if (requestError || !data || !data.label) {
      setError(
        requestError?.response?.data?.error ||
          'Could not analyze the selection. Is the analysis service running?'
      );
      return;
    }
    setResult(data);
  }, [selectedText]);

  if (!anchor) return null;

  const chipTop = Math.max(8, anchor.top - 44);
  const panelTop = Math.min(window.innerHeight - 60, anchor.bottom + 10);
  const clampedLeft = Math.min(Math.max(anchor.left, 130), window.innerWidth - 130);

  const meta = result ? SENTIMENT_META[result.label] || SENTIMENT_META.neutral : null;
  const wordCount = selectedText.split(/\s+/).filter(Boolean).length;

  return createPortal(
    <>
      {/* Floating trigger chip above the selection */}
      {!panelOpen && (
        <Box
          onMouseDown={(e) => e.preventDefault()} // keep the text selection
          onClick={handleAnalyze}
          role="button"
          aria-label="Analyze tone of selected text"
          sx={{
            position: 'fixed',
            top: chipTop,
            left: clampedLeft,
            transform: 'translateX(-50%)',
            zIndex: 1500,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 0.5,
            borderRadius: '999px',
            cursor: 'pointer',
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            color: colors?.text?.primary,
            border: `1px solid ${colors?.border?.subtle || 'rgba(148,163,184,0.4)'}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            fontSize: '0.8125rem',
            fontWeight: 500,
            userSelect: 'none',
            '&:hover': {
              borderColor: colors?.interactive?.primary || '#3b82f6',
              color: colors?.interactive?.primary || '#3b82f6',
            },
          }}
        >
          <InsightsIcon sx={{ fontSize: '1rem' }} />
          Analyze tone
        </Box>
      )}

      {/* Results panel below the selection */}
      {panelOpen && (
        <Paper
          onMouseDown={(e) => e.preventDefault()}
          elevation={8}
          sx={{
            position: 'fixed',
            top: panelTop,
            left: clampedLeft,
            transform: 'translateX(-50%)',
            zIndex: 1500,
            width: 300,
            p: 2,
            borderRadius: 2,
            backgroundColor: isDark ? '#1a1a2e' : '#ffffff',
            color: colors?.text?.primary,
            border: `1px solid ${colors?.border?.subtle || 'rgba(148,163,184,0.4)'}`,
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Tone analysis
            </Typography>
            <IconButton
              size="small"
              aria-label="Close tone analysis"
              onClick={() => {
                closePanel();
                setAnchor(null);
                setSelectedText('');
              }}
              sx={{ color: colors?.text?.secondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ color: colors?.text?.secondary }}>
                Analyzing {wordCount.toLocaleString()} words…
              </Typography>
            </Box>
          )}

          {!loading && error && (
            <Typography variant="body2" sx={{ color: '#ef4444' }}>
              {error}
            </Typography>
          )}

          {!loading && result && meta && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: meta.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {meta.label}
                </Typography>
                <Typography variant="body2" sx={{ color: colors?.text?.secondary }}>
                  {Math.round((result.confidence || 0) * 100)}% confident
                </Typography>
              </Box>

              {result.probabilities &&
                Object.entries(result.probabilities).map(([label, probability]) => {
                  const rowMeta = SENTIMENT_META[label] || SENTIMENT_META.neutral;
                  return (
                    <Box key={label} sx={{ mb: 0.75 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" sx={{ color: colors?.text?.secondary }}>
                          {rowMeta.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors?.text?.secondary }}>
                          {Math.round(probability * 100)}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: isDark
                            ? 'rgba(148,163,184,0.15)'
                            : 'rgba(148,163,184,0.25)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${Math.round(probability * 100)}%`,
                            backgroundColor: rowMeta.color,
                            borderRadius: 2,
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}

              <Typography
                variant="caption"
                sx={{
                  color: colors?.text?.muted || colors?.text?.secondary,
                  display: 'block',
                  mt: 1,
                }}
              >
                Based on {wordCount.toLocaleString()} selected words
              </Typography>
            </>
          )}
        </Paper>
      )}
    </>,
    document.body
  );
};

export default SentimentPlugin;
